import { Select, Text } from '@billy/design-system'
import type { ReactElement } from 'react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import type { InvoiceTotalPriceColumns } from '@features/invoices/enums/invoiceTotalPriceColumns'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { useFormContext } from '../../../../../../hooks'
import type { InvoicingSettingsSection } from '../../../../enums/invoicingSettingsSection'
import { useTrackInvoicingSettings } from '../../../../hooks/useTrackInvoicingSettings'
import type { DocumentTypeSettingsFormSchema } from '../../types/documentTypeSettingsFormSchema'
import { getTotalPriceColumnsItems } from './utils/getTotalPriceColumnsItems'

interface DocumentTypeSettingsTotalPriceColumnsProps {
  trackingSection: InvoicingSettingsSection
}

export const DocumentTypeSettingsTotalPriceColumns = ({
  trackingSection,
}: DocumentTypeSettingsTotalPriceColumnsProps): ReactElement | null => {
  const { t } = useTranslation()
  const { FormItem } = useFormContext<DocumentTypeSettingsFormSchema>()
  const { organization } = useUserOrganization()
  const { trackInvoicingSettings } = useTrackInvoicingSettings({ section: trackingSection })

  const totalPriceColumnsItems = useMemo(() => getTotalPriceColumnsItems(t), [t])

  if (organization?.isVatExempted) {
    return null
  }

  return (
    <FormItem
      label={<Text weight="medium">{t('invoicing_settings.document_type_settings.totals')}</Text>}
      name="totalPriceColumns"
      render={({ field: { onChange, value, ...rest } }) => (
        <Select<InvoiceTotalPriceColumns>
          {...rest}
          dropdownSize="fitTrigger"
          items={totalPriceColumnsItems}
          onSelect={(_id, totalPriceColumns) => {
            onChange(totalPriceColumns)
            trackInvoicingSettings('Total price columns selected', { totalPriceColumns })
          }}
          selectedId={value}
          withSearch={false}
        />
      )}
    />
  )
}
